// components/MegaMenu.tsx
'use client';

import * as React from 'react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { Category, Subcategory2 } from '@/types/schema';
import { Skeleton } from './ui/skeleton';

interface MegaNavProps {
  categories: Category[];
}

export function MegaNav({ categories }: MegaNavProps) {

    // State to store subcategories and loading status
  const [subcategoriesData, setSubcategoriesData] = React.useState<{
    [categoryId: string]: {
      data: Subcategory2[]
      loading: boolean
    }
  }>({})

  // Function to fetch subcategories
  const fetchSubcategories = async (categoryId: string) => {
    // Avoid refetching if already fetched
    if (subcategoriesData[categoryId]?.data.length > 0 || subcategoriesData[categoryId]?.loading) {
      return
    }
    // Set loading state
    setSubcategoriesData((prev) => ({
        ...prev,
        [categoryId]: { data: [], loading: true },
    }))

    try {
        const res = await fetch(`/api/subcategories?categoryId=${categoryId}`);
        if (!res.ok) {
        throw new Error('Network response was not ok')
        }
        const data: Subcategory2[] = await res.json()
        // Update state with fetched data
        setSubcategoriesData((prev) => ({
        ...prev,
        [categoryId]: { data, loading: false },
        }))
    } catch (error) {
        console.error('Failed to fetch subcategories:', error)
        // Update state to stop loading
        setSubcategoriesData((prev) => ({
        ...prev,
        [categoryId]: { data: [], loading: false },
        }))
    }
    }

  return (
    <NavigationMenu className="hidden md:flex static">
      <NavigationMenuList className="flex justify-start w-full max-w-full px-2">
        {categories.map((category) => (
          <NavigationMenuItem key={category._id} onMouseEnter={() => fetchSubcategories(category._id)}>
            <NavigationMenuTrigger className="text-sm font-medium px-4 py-2 text-slate-600 hover:text-slate-900 focus:text-primary">
              {category.title}
            </NavigationMenuTrigger>
            <NavigationMenuContent className="!w-[99vw] z-50 px-4">
              {/* Check if subcategories are loading */}
              {subcategoriesData[category._id]?.loading? (
                <div className="w-full max-w-5xl mx-auto px-6 py-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
                    {/* Render skeletons */}
                    {Array.from({ length: 8 }).map((_, idx) => (
                      <div key={idx} className="flex items-start space-x-3 p-4 rounded-md">
                        <Skeleton className="w-12 h-12 rounded-md bg-muted" />
                        <div className="flex-1 space-y-1">
                          <Skeleton className="w-24 h-4" />
                          <Skeleton className="w-32 h-3" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                // Render subcategories if available
                <div className="w-full max-w-6xl mx-auto px-6 py-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
                    {subcategoriesData[category._id]?.data.map((subcategory) => (
                      <a
                        key={subcategory._id}
                        href={`/prompt-category/${category.value.current}/${subcategory.value.current}`}
                        className="flex items-start space-x-3 p-4 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors duration-200"
                      >
                        <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-md bg-muted" aria-hidden="true">
                        <span className="text-xl">{subcategory.icon}</span>
                        </div>
                        {/* Title and Description */}
                        <div className="flex-1">
                        <h3 className="text-sm font-semibold">{subcategory.title}</h3>
                        <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                            {subcategory.description}
                        </p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </NavigationMenuContent>
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  );
}
